/**
 * Scroll Product Showcase
 * Handles scroll-based animations for product showcase section
 */

class ScrollProductShowcase {
  constructor(element) {
    this.section = element;
    this.cards = Array.from(this.section.querySelectorAll('.scroll-product-showcase__card'));
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.isMobile = window.matchMedia('(max-width: 767px)').matches;
    
    // Animation state
    this.isAnimating = false;
    this.animationFrame = null;
    
    // Bind methods
    this.handleScroll = this.handleScroll.bind(this);
    this.handleResize = this.debounce(this.handleResize.bind(this), 200);
    
    // Initialize
    this.init();
  }

  init() {
    if (this.prefersReducedMotion || this.cards.length === 0) {
      // Show all cards immediately if reduced motion is preferred
      this.cards.forEach(card => {
        card.classList.add('is-visible');
      });
      return;
    }
    
    // Add event listeners
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    window.addEventListener('resize', this.handleResize);
    
    // Initial check - run immediately to show cards already in view
    if (this.isMobile) {
      this.animateCardsMobile();
    } else {
      this.animateCardsDesktop();
    }
  }

  handleScroll() {
    if (this.isAnimating) return;
    
    this.isAnimating = true;
    this.animationFrame = requestAnimationFrame(() => {
      if (this.isMobile) {
        this.animateCardsMobile();
      } else {
        this.animateCardsDesktop();
      }
      this.isAnimating = false;
    });
  }

  animateCardsDesktop() {
    const viewportHeight = window.innerHeight;
    const triggerPointEnter = viewportHeight * 0.85;
    const exitPoint = viewportHeight * 0.2;
    
    this.cards.forEach(card => {
      const cardRect = card.getBoundingClientRect();
      const cardTop = cardRect.top;
      const cardBottom = cardRect.bottom;
      const cardCenter = cardTop + (cardRect.height / 2);
      
      if (cardTop < triggerPointEnter && cardBottom > 0 && !card.classList.contains('is-visible')) {
        card.classList.add('is-visible');
        card.classList.remove('is-exiting');
      }
      
      if (cardCenter < exitPoint && card.classList.contains('is-visible')) {
        card.classList.add('is-exiting');
      }
      
      if (cardCenter >= exitPoint && cardCenter < triggerPointEnter && card.classList.contains('is-exiting')) {
        card.classList.remove('is-exiting');
      }
    });
  }

  animateCardsMobile() {
    const sectionRect = this.section.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const sectionHeight = sectionRect.height;
    
    // Only animate when section is in viewport (with some buffer)
    if (sectionRect.bottom < 0 || sectionRect.top > viewportHeight) {
      return;
    }
    
    // Calculate scroll progress (0 to 1) through the section
    // We want the animation to start as soon as the section hits the top (or slightly before)
    // and end when the section leaves
    const scrollableDistance = sectionHeight - viewportHeight;
    // Start animation earlier to account for header overlap (approx 80px + buffer)
    const startOffset = 500; 
    const scrolled = -sectionRect.top + startOffset;
    // Include startOffset in denominator so progress reaches 1 exactly at the end of scroll
    // Added extra buffer (200px) to denominator to ensure animation finishes slightly BEFORE the very end
    const scrollProgress = Math.max(0, Math.min(1, scrolled / (scrollableDistance + startOffset + 300)));
    
    // Filter out cards that are hidden on mobile
    const visibleCards = this.cards.filter(card => !card.classList.contains('hide-on-mobile'));
    const totalCards = visibleCards.length;
    
    // Update card count CSS variable for scroll height calculation
    this.section.querySelector('.scroll-product-showcase__cards-wrapper').style.setProperty('--card-count', totalCards);
    
    if (totalCards === 0) return;
    
    // Determine which card is currently active
    // We map the scroll progress 0-1 to card indices range [-1, totalCards - 1]
    // -1 means no card is active yet (all at bottom)
    const activeCardIndex = Math.max(
      -1,
      Math.min(
        totalCards - 1,
        Math.floor(scrollProgress * (totalCards + 1)) - 1
      )
    );
    
    visibleCards.forEach((card, index) => {
      // Remove all state classes first
      card.classList.remove('is-at-bottom', 'is-at-center', 'is-at-top', 'is-hidden', 'is-active', 'is-previous', 'is-upcoming', 'is-past');
      
      if (index < activeCardIndex - 1) {
        // Cards older than the immediate previous one - hide completely
        card.classList.add('is-past');
        card.style.removeProperty('--stack-index');
        card.style.removeProperty('--active-progress');
      } else if (index === activeCardIndex - 1) {
        // Immediate previous card
        card.classList.add('is-previous');
        card.style.removeProperty('--stack-index');
        card.style.removeProperty('--active-progress');
      } else if (index === activeCardIndex) {
        // Active card
        card.classList.add('is-active');
        card.style.removeProperty('--stack-index');
        
        // Calculate progress for this specific card (0 to 1)
        const totalSteps = totalCards + 1;
        const currentStep = scrollProgress * totalSteps;
        // when index == activeCardIndex, currentStep is roughly index + 1 to index + 2
        // progress = currentStep - (activeCardIndex + 1)
        const progress = Math.max(0, Math.min(1, currentStep - (activeCardIndex + 1)));
        card.style.setProperty('--active-progress', progress);
      } else {
        // Upcoming cards
        card.classList.add('is-upcoming');
        // Stack index determines how far down it is pushed
        // 0 is the immediate next card, 1 is the one after, etc.
        const stackIndex = index - activeCardIndex;
        card.style.setProperty('--stack-index', stackIndex);
        card.style.removeProperty('--active-progress');
      }
    });
  }

  // Easing function for smooth animation
  easeInOutCubic(t) {
    return t < 0.5
      ? 4 * t * t * t
      : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  handleResize() {
    // Update mobile state
    const wasMobile = this.isMobile;
    this.isMobile = window.matchMedia('(max-width: 767px)').matches;
    
    // Reset card styles if switching between mobile/desktop
    if (wasMobile !== this.isMobile) {
      this.cards.forEach(card => {
        card.classList.remove('is-visible', 'is-exiting');
        card.style.transform = '';
      });
    }
    
    // Recalculate on resize
    this.handleScroll();
  }

  // Utility: Debounce function
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // Cleanup method
  destroy() {
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleResize);
    
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }
  }
}

// Initialize all scroll product showcase sections
function initScrollProductShowcase() {
  const sections = document.querySelectorAll('.scroll-product-showcase');
  const instances = [];
  
  sections.forEach(section => {
    instances.push(new ScrollProductShowcase(section));
  });
  
  // Store instances for potential cleanup
  window.scrollProductShowcaseInstances = instances;
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initScrollProductShowcase);
} else {
  initScrollProductShowcase();
}

// Shopify theme editor support
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', function(event) {
    const section = event.target.querySelector('.scroll-product-showcase');
    if (section) {
      new ScrollProductShowcase(section);
    }
  });
  
  document.addEventListener('shopify:section:unload', function(event) {
    // Clean up instances if needed
    if (window.scrollProductShowcaseInstances) {
      window.scrollProductShowcaseInstances.forEach(instance => {
        if (instance.section.closest(`[data-section-id="${event.detail.sectionId}"]`)) {
          instance.destroy();
        }
      });
    }
  });
}

